/**************************************************************************************************/
/*   File: /src/app/stores/effects/editing/effects.ts                                             */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Monday April 29th 2019                                                         */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Mon Jun 17 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Observable, of, EMPTY, empty } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';
import { RootState } from '../../states/root';
import { Store } from '@ngrx/store';

/**
 * The router effect
 */
@Injectable()
export class EditingEffects {

  /**
   * Check dossier status effect
   */
  @Effect()
  checkDossierStatusEffect$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.CheckDossierStatus>(
      fromActions.fromEditing.DossierEditingActionsType.CHECK_DOSSIER_STATUS
    ),
    switchMap((action: fromActions.fromEditing.CheckDossierStatus) =>
      this.dossierService.checkstatus(action.payload.dossierId).pipe(
        map((dossierStatus: fromShared.models.DossierStatus) =>
          new fromActions.fromEditing.CheckDossierStatusSuccess({
            status: dossierStatus
          })
        ),
        catchError(() =>
          of(
            new fromActions.fromEditing.CheckDossierStatusFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_CHECK_DOSSIER_STATUS,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            })
          )
        )
      )
    )
  );
  /**
   * The load dossier effect
   */
  @Effect()
  loadDossierEffect$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.LoadDossier>(
      fromActions.fromEditing.DossierEditingActionsType.LOAD_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.LoadDossier) =>
      this.dossierService.getDossierById(action.payload.dossierId).pipe(
        mergeMap(
          (dossier: fromShared.models.Dossier) => [
            new fromActions.fromEditing.LoadDossierSuccess({
              dossier: dossier
            }),
            new fromActions.fromEditing.CheckDossierStatus({
              dossierId: {
                id: dossier.summary.id
              }
            })
          ]
        )
      )
    )
  );
  /**
   * The validate dossier effect
   */
  @Effect()
  validateDossierEffect$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.ValidateDossier>(
      fromActions.fromEditing.DossierEditingActionsType.VALIDATE_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.ValidateDossier) =>
      this.dossierService.validate(action.payload.dossierId).pipe(
        map(
          (result: fromShared.models.Result) => {
            if (result.result) {
              return new fromActions.fromEditing.ValidateDossierSuccess({
                dossierId: action.payload.dossierId
              });
            }

            return new fromActions.fromEditing.ValidateDossierFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_VALIDATE_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            });
          }
        ),
        catchError(() =>
          of(
            new fromActions.fromEditing.ValidateDossierFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_VALIDATE_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            })
          )
        )
      )
    )
  );
  /**
  * The send dossier effect
  */
  @Effect()
  validateDossierEffectSuccess$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.ValidateDossierSuccess>(
      fromActions.fromEditing.DossierEditingActionsType.VALIDATE_DOSSIER_SUCCESS
    ),
    mergeMap(
      (action: fromActions.fromEditing.ValidateDossierSuccess) => [
        new fromActions.fromRouter.Go({
          path: [
            'shell',
            'details',
            action.payload.dossierId.id
          ]
        })
      ]
    )
  );
  /**
   * The send dossier effect
   */
  @Effect()
  deleteDossierEffect$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.DeleteDossier>(
      fromActions.fromEditing.DossierEditingActionsType.DELETE_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.DeleteDossier) =>
      this.dossierService.delete(action.payload.dossierId).pipe(
        map(
          (result: fromShared.models.Result) => {
            if (result.result) {
              return new fromActions.fromEditing.DeleteDossierSuccess({
                result: result
              });
            }

            return new fromActions.fromEditing.DeleteDossierFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_DELETE_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            });
          }
        ),
        catchError(() =>
          of(
            new fromActions.fromEditing.DeleteDossierFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_DELETE_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            })
          )
        )
      )
    )
  );
  /**
  * The send dossier effect
  */
  @Effect()
  deleteDossierEffectSuccess$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.DeleteDossierSuccess>(
      fromActions.fromEditing.DossierEditingActionsType.DELETE_DOSSIER_SUCCESS
    ),
    mergeMap(
      (action: fromActions.fromEditing.DeleteDossierSuccess) => [
        new fromActions.fromRouter.Go({
          path: [
            'shell',
            'dossiers'
          ]
        })
      ]
    )
  );
  /**
    * The spatial file upload request effect.
    * If file creation goes well then dispatch file upload action success, else file upload action fail
    */
  @Effect()
  spatialFileUpload$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.SpatialFileUploadAction>(
      fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_UPLOAD
    ),
    switchMap((action: fromActions.fromEditing.SpatialFileUploadAction) =>
      this.fileService.createFile(action.payload.file).pipe(
        map((response: any) =>
          // IMPROVEMENT Here goes the tow step file upload if needed
          new fromActions.fromEditing.SpatialFileUploadActionSuccess(
            {
              file: action.payload.file,
              documentId: response
            }
          )
        ),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.SpatialFileUploadActionFail(
              {
                file: action.payload.file,
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPLOAD_REQUESTED_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The remove spatialfile request.
   * If delete document goes well dispatch file removed success else file removed fail.
   * If response from the service has a falsy result then dispatch file removed action fail
   */
  @Effect()
  spatialFileRemove$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.SpatialFileRemovedAction>(
      fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_REMOVED
    ),
    switchMap((action: fromActions.fromEditing.SpatialFileRemovedAction) =>
      this.fileService.deleteDocument(action.payload.id, action.payload.isUploaded).pipe(
        map((response: any) => {

          if (response.result) {
            // IMPROVEMENT Here goes the tow step file upload if needed
            return new fromActions.fromEditing.SpatialFileRemovedSuccessAction(
              {
                id: action.payload.id
              }
            );
          } else {
            return new fromActions.fromEditing.SpatialFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_REMOVE_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            );
          }

        }),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.SpatialFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
   * The clear spatial files request.
   * Dispatch a file removed action for each file passed through action.
   */
  @Effect()
  spatialFileClear$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.SpatialFileClearAction>(
      fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_CLEAR
    ),
    mergeMap(
      (action: fromActions.fromEditing.SpatialFileClearAction) => {
        const arrayOfActions = [];
        action.payload.files.forEach((file) => {
          arrayOfActions.push(
            new fromActions.fromEditing.SpatialFileRemovedAction({
              id: file.id,
              isUploaded: false // file.isUploaded
            })
          );
        });
        return arrayOfActions;
      }
    )
  );
  /**
    * The other file upload request effect.
    * If file creation goes well then dispatch file upload action success, else file upload action fail
    */
  @Effect()
  otherFileUpload$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.OtherFileUploadAction>(
      fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_UPLOAD
    ),
    switchMap((action: fromActions.fromEditing.OtherFileUploadAction) =>
      this.fileService.createFile(action.payload.file).pipe(
        map((response: any) =>
          // IMPROVEMENT Here goes the tow step file upload if needed
          new fromActions.fromEditing.OtherFileUploadActionSuccess(
            {
              file: action.payload.file,
              documentId: response
            }
          )
        ),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.OtherFileUploadActionFail(
              {
                file: action.payload.file,
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPLOAD_REQUESTED_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
  * The remove other file request.
  * If delete document goes well dispatch file removed success else file removed fail.
  * If response from the service has a falsy result then dispatch file removed action fail
  */
  @Effect()
  otherFileRemove$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.OtherFileRemovedAction>(
      fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_REMOVED
    ),
    switchMap((action: fromActions.fromEditing.OtherFileRemovedAction) =>
      this.fileService.deleteDocument(action.payload.id, action.payload.isUploaded).pipe(
        map((response: any) => {

          if (response.result) {
            // IMPROVEMENT Here goes the tow step file upload if needed
            return new fromActions.fromEditing.OtherFileRemovedSuccessAction(
              {
                id: action.payload.id
              }
            );
          } else {
            return new fromActions.fromEditing.OtherFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_REMOVE_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            );
          }

        }),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.OtherFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
   * The clear other files request.
   * Dispatch a file removed action for each file passed through action.
   */
  @Effect()
  otherFileClear$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.OtherFileClearAction>(
      fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_CLEAR
    ),
    mergeMap(
      (action: fromActions.fromEditing.OtherFileClearAction) => {
        const arrayOfActions = [];
        action.payload.files.forEach((file) => {
          arrayOfActions.push(
            new fromActions.fromEditing.OtherFileRemovedAction({
              id: file.id,
              isUploaded: false // file.isUploaded
            })
          );
        });
        return arrayOfActions;
      }
    )
  );
  /**
   * The regulation file upload request effect.
   * If file creation goes well then dispatch file upload action success, else file upload action fail
   */
  @Effect()
  regulationFileUpload$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.RegulationFileUploadAction>(
      fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_UPLOAD
    ),
    switchMap((action: fromActions.fromEditing.RegulationFileUploadAction) =>
      this.fileService.createFile(action.payload.file).pipe(
        map((response: any) =>
          // IMPROVEMENT Here goes the tow step file upload if needed
          new fromActions.fromEditing.RegulationFileUploadActionSuccess(
            {
              file: action.payload.file,
              type: action.payload.type,
              areaCode: action.payload.areaCode,
              documentId: response
            }
          )
        ),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.RegulationFileUploadActionFail(
              {
                file: action.payload.file,
                type: action.payload.type,
                areaCode: action.payload.areaCode,
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPLOAD_REQUESTED_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
  * The remove regulation file request.
  * If delete document goes well dispatch file removed success else file removed fail.
  * If response from the service has a falsy result then dispatch file removed action fail
  */
  @Effect()
  regulationFileRemove$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.RegulationFileRemovedAction>(
      fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_REMOVED
    ),
    switchMap((action: fromActions.fromEditing.RegulationFileRemovedAction) =>
      this.fileService.deleteDocument(action.payload.id, action.payload.isUploaded).pipe(
        map((response: any) => {

          if (response.result) {
            return new fromActions.fromEditing.RegulationFileRemovedSuccessAction(
              {
                id: action.payload.id,
                type: action.payload.type,
                areaCode: action.payload.areaCode,
              }
            );
          } else {
            return new fromActions.fromEditing.RegulationFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_REMOVE_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            );
          }

        }),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.RegulationFileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
   * The clear regulation files request.
   * Dispatch a file removed action for each file passed through action.
   */
  @Effect()
  regulationFileClear$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.RegulationFileClearAction>(
      fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_CLEAR
    ),
    mergeMap(
      (action: fromActions.fromEditing.RegulationFileClearAction) => {
        const arrayOfActions = [];
        action.payload.files.forEach((file) => {
          arrayOfActions.push(
            new fromActions.fromEditing.RegulationFileRemovedAction({
              id: file.id,
              type: action.payload.type,
              areaCode: action.payload.areaCode,
              isUploaded: false // file.isUploaded
            })
          );
        });
        return arrayOfActions;
      }
    )
  );
  /**
   * The spatial file update request effect.
   * If file creation or update goes well then dispatch update spatial data action success, else upload spatial data action fail
   */
  @Effect()
  updateSpatialDataDossier$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.UpdateSpatialDataDossier>(
      fromActions.fromEditing.DossierEditingActionsType.UPDATE_SPATIAL_DATA_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.UpdateSpatialDataDossier) =>
      this.dossierService.createOrUpdateSpatialData(
        action.payload.dossierId,
        action.payload.spatialDataType,
        action.payload.spatialDataId
      ).pipe(
        mergeMap((response: fromShared.models.Result) => [
          // IMPROVEMENT Here goes the tow step file upload if needed
          new fromActions.fromEditing.UpdateSpatialDataDossierSuccess(),
          new fromActions.fromEditing.LoadDossier({
            dossierId: {
              id: action.payload.dossierId
            }
          })
        ]),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.UpdateSpatialDataDossierFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPDATE_SPATIAL,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
  * The other file update request effect.
  * If file creation or update goes well then dispatch update other data action success, else upload other data action fail
  */
  @Effect()
  updateOtherDataDossier$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.UpdateOtherDataDossier>(
      fromActions.fromEditing.DossierEditingActionsType.UPDATE_OTHER_DATA_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.UpdateOtherDataDossier) =>
      this.dossierService.createOrUpdateOtherData(action.payload.dossierId, action.payload.otherDataId).pipe(
        mergeMap((response: fromShared.models.Result) =>
          [
            // IMPROVEMENT Here goes the tow step file upload if needed
            new fromActions.fromEditing.UpdateOtherDataDossierSuccess(),
            new fromActions.fromEditing.LoadDossier({
              dossierId: {
                id: action.payload.dossierId
              }
            })
          ]
        ),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.UpdateOtherDataDossierFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPDATE_OTHER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The regulation file update request effect.
   * If file creation or update goes well then dispatch update other data action success, else upload other data action fail
   */
  @Effect()
  updateRegulationDataDossier$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.UpdateRegulationDataDossier>(
      fromActions.fromEditing.DossierEditingActionsType.UPDATE_REGULATION_DATA_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.UpdateRegulationDataDossier) =>
      this.dossierService.createOrUpdateRegulationsData(
        action.payload.dossierId,
        action.payload.territorialAreaCode,
        action.payload.originalDoc,
        action.payload.deltaDoc,
        action.payload.finalDoc).pipe(
          mergeMap((response: fromShared.models.Result) =>
            [
              // IMPROVEMENT Here goes the tow step file upload if needed
              new fromActions.fromEditing.UpdateRegulationDataDossierSuccess(),
              new fromActions.fromEditing.LoadDossier({
                dossierId: {
                  id: action.payload.dossierId
                }
              })
            ]
          ),
          catchError((failure) =>
            of(
              new fromActions.fromEditing.UpdateRegulationDataDossierFail(
                {
                  message: new fromShared.models.Message(
                    fromShared.enums.MessageType.ERROR,
                    fromShared.enums.Messages.CANNOT_UPDATE_REGULATION,
                    fromShared.enums.MessagePositionEnum.TOP,
                    fromStates.fromEditing.STATE_NAME
                  )
                }
              )
            )
          )
        )
    )
  );
  /**
  * The send dossier request effect.
  * If file creation or update goes well then dispatch update other data action success, else upload other data action fail
  */
  @Effect()
  sendDossier$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.SendDossier>(
      fromActions.fromEditing.DossierEditingActionsType.SEND_DOSSIER
    ),
    switchMap((action: fromActions.fromEditing.SendDossier) =>
      this.dossierService.delivery(action.payload.changeStatus).pipe(
        map((result: fromShared.models.Result) => {
          if (result.result) {
            return new fromActions.fromEditing.SendDossierSuccess();
          }
          return new fromActions.fromEditing.SendDossierFail(
            {
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_SEND_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromEditing.STATE_NAME
              )
            }
          );
        }),
        catchError((failure) =>
          of(
            new fromActions.fromEditing.SendDossierFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_SEND_DOSSIER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromEditing.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The send dossier request effect.
   * If file creation or update goes well then dispatch update other data action success, else upload other data action fail
   */
  @Effect()
  sendDossierSuccess$ = this.actions$.pipe(
    ofType<fromActions.fromEditing.SendDossierSuccess>(
      fromActions.fromEditing.DossierEditingActionsType.SEND_DOSSIER_SUCCESS
    ),
    mergeMap((action: fromActions.fromEditing.SendDossierSuccess) =>
      [
        new fromActions.fromRouter.Go({
          path: [
            'shell',
            'dossiers'
          ]
        })
      ]
    )
  );

  /**
   * The contructor
   * @param store The store
   * @param actions$ The actions
   * @param router The router
   * @param location The location
   */
  constructor(
    private actions$: Actions,
    private dossierService: fromShared.services.DossierService,
    private fileService: fromShared.services.FileService,
  ) { }

}
